import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';

const toDateTimeInputValue = date => {
  if (!(date instanceof Date)) return '';
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 19);
};

const parseDateTime = value => {
  if (!value) return null;
  const withSeconds = value.length === 16 ? `${value}:00` : value;
  const parsed = new Date(withSeconds);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDisplayDate = value =>
  value
    ? value.toLocaleString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      })
    : '';

const describeDuration = (startDate, endDate) => {
  const diffMs = endDate.getTime() - startDate.getTime();
  if (diffMs < 0) return '';
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];
  if (days) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (hours) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  if (minutes) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  if (!parts.length) {
    parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
  }
  return parts.join(' ');
};

function DateRangePicker({ initialStart, initialEnd, onApply, onClose }) {
  const quickRanges = useMemo(
    () => [
      {
        label: 'Custom',
        description: 'Manually choose a start and end date & time.',
        isCustom: true,
      },
      {
        label: 'Yesterday',
        description: 'Data recorded yesterday.',
        range: () => {
          const start = new Date();
          start.setDate(start.getDate() - 1);
          start.setHours(0, 0, 0, 0);
          const end = new Date(start);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        },
      },
      {
        label: 'Last 7 days',
        description: 'Rolling window including today.',
        range: () => {
          const end = new Date();
          end.setHours(23, 59, 59, 999);
          const start = new Date(end);
          start.setDate(start.getDate() - 6);
          start.setHours(0, 0, 0, 0);
          return { start, end };
        },
      },
      {
        label: 'Last 14 days',
        description: 'Two week look-back.',
        range: () => {
          const end = new Date();
          end.setHours(23, 59, 59, 999);
          const start = new Date(end);
          start.setDate(start.getDate() - 13);
          start.setHours(0, 0, 0, 0);
          return { start, end };
        },
      },
      {
        label: 'Last 30 days',
        description: 'Thirty day rolling window.',
        range: () => {
          const end = new Date();
          end.setHours(23, 59, 59, 999);
          const start = new Date(end);
          start.setDate(start.getDate() - 29);
          start.setHours(0, 0, 0, 0);
          return { start, end };
        },
      },
      {
        label: 'This month',
        description: 'From the 1st of this month.',
        range: () => {
          const start = new Date();
          start.setDate(1);
          start.setHours(0, 0, 0, 0);
          const end = new Date();
          end.setHours(23, 59, 59, 999);
          return { start, end };
        },
      },
      {
        label: 'Previous month',
        description: 'Entire previous calendar month.',
        range: () => {
          const now = new Date();
          const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          start.setHours(0, 0, 0, 0);
          const end = new Date(now.getFullYear(), now.getMonth(), 0);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        },
      },
    ],
    []
  );

  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(initialEnd);
  const [activeQuick, setActiveQuick] = useState(() => {
    const match = quickRanges.find(range => {
      if (range.isCustom || !range.range) return false;
      const { start: s, end: e } = range.range();
      return (
        toDateTimeInputValue(s) === initialStart && toDateTimeInputValue(e) === initialEnd
      );
    });
    return match ? match.label : 'Custom';
  });

  const startDate = parseDateTime(start);
  const endDate = parseDateTime(end);
  const hasError = startDate && endDate && endDate < startDate;
  const disabled = !startDate || !endDate || hasError;
  const durationLabel = !disabled ? describeDuration(startDate, endDate) : '';

  useEffect(() => {
    const match = quickRanges.find(range => {
      if (range.isCustom || !range.range) return false;
      const { start: s, end: e } = range.range();
      return toDateTimeInputValue(s) === start && toDateTimeInputValue(e) === end;
    });
    const label = match ? match.label : 'Custom';
    setActiveQuick(curr => (curr === label ? curr : label));
  }, [start, end, quickRanges]);

  const applyQuickRange = range => {
    if (range.isCustom || !range.range) {
      setActiveQuick(range.label);
      return;
    }
    const { start: s, end: e } = range.range();
    setStart(toDateTimeInputValue(s));
    setEnd(toDateTimeInputValue(e));
    setActiveQuick(range.label);
  };

  const apply = () => {
    if (disabled) return;
    onApply(start, end);
    onClose();
  };

  const resetSelection = () => {
    setStart(initialStart);
    setEnd(initialEnd);
  };

  return (
    <Transition appear show as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/80" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-[#081620] text-white shadow-2xl ring-1 ring-white/10 transition-all">
                <div className="border-b border-white/5 bg-gradient-to-r from-[#0b2030] to-[#12344a] px-6 py-5 text-left">
                  <Dialog.Title className="text-lg font-semibold">Custom time range</Dialog.Title>
                  <p className="mt-1 text-sm text-slate-300">
                    Choose one of the quick presets or specify the exact start and end dates and
                    times below.
                  </p>
                </div>

                <div className="p-6">
                  <div className="grid gap-6 md:grid-cols-[220px_1fr]">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                        Quick ranges
                      </p>
                      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-1">
                        {quickRanges.map(range => (
                          <button
                            key={range.label}
                            type="button"
                            onClick={() => applyQuickRange(range)}
                            className={`rounded-xl border px-4 py-3 text-left transition-all duration-150 ${
                              activeQuick === range.label
                                ? 'border-[#22c55e]/80 bg-[#22c55e]/10 text-white shadow-lg shadow-[#22c55e]/10'
                                : 'border-white/5 bg-white/[0.02] text-slate-200 hover:border-[#22c55e]/40 hover:bg-[#22c55e]/5'
                            }`}
                          >
                            <div className="text-sm font-semibold">{range.label}</div>
                            <div className="text-xs text-slate-400">{range.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col space-y-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="flex flex-col space-y-2">
                          <label className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
                            Start date &amp; time
                          </label>
                          <input
                            type="datetime-local"
                            value={start || ''}
                            onChange={e => setStart(e.target.value)}
                            step="1"
                            className="w-full rounded-xl border border-white/10 bg-[#0b1d2b] px-3 py-2 text-sm text-white shadow-inner [color-scheme:dark] focus:border-[#22c55e]/60 focus:outline-none focus:ring-2 focus:ring-[#22c55e]/40"
                          />
                        </div>
                        <div className="flex flex-col space-y-2">
                          <label className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
                            End date &amp; time
                          </label>
                          <input
                            type="datetime-local"
                            value={end || ''}
                            onChange={e => setEnd(e.target.value)}
                            step="1"
                            className="w-full rounded-xl border border-white/10 bg-[#0b1d2b] px-3 py-2 text-sm text-white shadow-inner [color-scheme:dark] focus:border-[#22c55e]/60 focus:outline-none focus:ring-2 focus:ring-[#22c55e]/40"
                          />
                        </div>
                      </div>

                      <div className="rounded-xl border border-white/5 bg-black/20 p-4 text-left">
                        {startDate && endDate ? (
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-white">
                              {`${formatDisplayDate(startDate)} → ${formatDisplayDate(endDate)}`}
                            </p>
                            <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
                              Duration: {durationLabel || '0 seconds'} selected
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-300">
                            Select both dates and times to preview the range.
                          </p>
                        )}
                      </div>

                      {hasError && (
                        <p className="text-xs font-medium text-rose-300">
                          End date must be on or after the start date.
                        </p>
                      )}

                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <button
                          type="button"
                          onClick={resetSelection}
                          className="inline-flex items-center justify-center rounded-lg border border-white/10 px-3 py-2 text-sm font-medium text-slate-200 transition hover:border-[#22c55e]/60 hover:text-white"
                        >
                          Reset to current selection
                        </button>
                        <span className="text-xs text-slate-400">
                          Tip: use a quick range to populate the fields, then fine-tune the exact
                          time if needed.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-white/5 bg-[#06101a] px-6 py-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-white/10 px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-white/30 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={apply}
                    disabled={disabled}
                    className="rounded-xl bg-gradient-to-r from-[#22c55e] to-[#16a34a] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-[#22c55e]/30 transition hover:from-[#16a34a] hover:to-[#15803d] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                  >
                    Apply range
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default DateRangePicker;
